import React, { useState } from 'react';
import { Sensor, SENSOR_DEFAULTS, useMachines } from '../context/MachineContext';
import { ToastType } from './Toast';
import './SensorsPanel.css';

interface Props {
  machineId:  string;
  sensors:    Sensor[];
  isReadOnly: boolean;
  onToast:    (msg: string, type?: ToastType) => void;
}

const SENSOR_ICONS: Record<Sensor['type'], string> = {
  temperature: '🌡️', vibration: '📳', rpm: '⚙️',
  pressure: '🔵', humidity: '💧', current: '⚡',
};

const SENSOR_TYPES = Object.keys(SENSOR_DEFAULTS) as Sensor['type'][];

interface SensorForm {
  name:             string;
  type:             Sensor['type'];
  unit:             string;
  minThreshold:     string;
  maxThreshold:     string;
  samplingInterval: string;
}

const SensorsPanel: React.FC<Props> = ({ machineId, sensors, isReadOnly, onToast }) => {
  const { addSensor, deleteSensor } = useMachines();
  const [adding, setAdding] = useState(false);
  const [form, setForm]     = useState<SensorForm>({
    name: '', type: 'temperature', unit: '°C',
    minThreshold: '0', maxThreshold: '100', samplingInterval: '5',
  });
  const [errors, setErrors] = useState<Partial<SensorForm>>({});

  const handleTypeChange = (type: Sensor['type']) => {
    const defaults = SENSOR_DEFAULTS[type];
    setForm({
      name: '', type,
      unit:             defaults.unit,
      minThreshold:     String(defaults.minThreshold),
      maxThreshold:     String(defaults.maxThreshold),
      samplingInterval: String(defaults.samplingInterval),
    });
    setErrors({});
  };

  const validate = (): boolean => {
    const e: Partial<SensorForm> = {};
    if (!form.name.trim())                    e.name = 'Required';
    if (isNaN(Number(form.minThreshold)))     e.minThreshold = 'Must be a number';
    if (isNaN(Number(form.maxThreshold)))     e.maxThreshold = 'Must be a number';
    if (Number(form.minThreshold) >= Number(form.maxThreshold)) e.maxThreshold = 'Max must be > min';
    if (Number(form.samplingInterval) < 1)    e.samplingInterval = 'Min 1 second';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAdd = () => {
    if (!validate()) return;
    addSensor(machineId, {
      name:             form.name,
      type:             form.type,
      unit:             form.unit,
      minThreshold:     Number(form.minThreshold),
      maxThreshold:     Number(form.maxThreshold),
      samplingInterval: Number(form.samplingInterval),
    });
    onToast(`Sensor "${form.name}" added`);
    setAdding(false);
    setForm({ name: '', type: 'temperature', unit: '°C', minThreshold: '0', maxThreshold: '100', samplingInterval: '5' });
  };

  const handleDelete = (s: Sensor) => {
    deleteSensor(machineId, s.sensorId);
    onToast(`Sensor "${s.name}" removed`, 'info');
  };

  const isAlert = (s: Sensor) =>
    s.currentValue !== undefined &&
    (s.currentValue > s.maxThreshold || s.currentValue < s.minThreshold);

  return (
    <div className="sensors-panel">
      <div className="sensors-header">
        <h4>📡 Sensors ({sensors.length})</h4>
        {!isReadOnly && !adding && (
          <button className="btn-add-sensor" onClick={() => setAdding(true)}>+ Add Sensor</button>
        )}
      </div>

      {sensors.length === 0 && !adding && (
        <div className="sensors-empty">
          No sensors configured.{!isReadOnly && ' Click "+ Add Sensor" to start.'}
        </div>
      )}

      <div className="sensor-list">
        {sensors.map(s => (
          <div key={s.sensorId} className={`sensor-row ${isAlert(s) ? 'sensor-alert' : ''}`}>
            <span className="sensor-icon">{SENSOR_ICONS[s.type]}</span>
            <div className="sensor-info">
              <span className="sensor-name">{s.name}</span>
              <span className="sensor-meta">{s.type} · {s.unit} · every {s.samplingInterval}s</span>
            </div>
            <div className="sensor-thresholds">
              <span>{s.minThreshold}–{s.maxThreshold} {s.unit}</span>
              {s.currentValue !== undefined && (
                <span className={isAlert(s) ? 'val-alert' : 'val-ok'}>
                  {isAlert(s) ? '⚠️' : '✓'} {s.currentValue} {s.unit}
                </span>
              )}
            </div>
            {!isReadOnly && (
              <button className="sensor-delete" onClick={() => handleDelete(s)} title="Remove sensor">✕</button>
            )}
          </div>
        ))}
      </div>

      {adding && (
        <div className="sensor-add-form">
          <div className="sensor-form-row">
            <div className="sensor-form-group">
              <label>Type</label>
              <select value={form.type} onChange={e => handleTypeChange(e.target.value as Sensor['type'])}>
                {SENSOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className={`sensor-form-group ${errors.name ? 'has-error' : ''}`}>
              <label>Sensor Name *</label>
              <input
                value={form.name}
                onChange={e => { setForm(p => ({ ...p, name: e.target.value })); setErrors(p => ({ ...p, name: undefined })); }}
                placeholder={`e.g. Motor ${form.type}`}
                autoFocus
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>
            <div className="sensor-form-group">
              <label>Unit</label>
              <input value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} />
            </div>
          </div>
          <div className="sensor-form-row">
            <div className={`sensor-form-group ${errors.minThreshold ? 'has-error' : ''}`}>
              <label>Min Threshold</label>
              <input type="number" value={form.minThreshold}
                onChange={e => { setForm(p => ({ ...p, minThreshold: e.target.value })); setErrors(p => ({ ...p, minThreshold: undefined })); }} />
              {errors.minThreshold && <span className="field-error">{errors.minThreshold}</span>}
            </div>
            <div className={`sensor-form-group ${errors.maxThreshold ? 'has-error' : ''}`}>
              <label>Max Threshold</label>
              <input type="number" value={form.maxThreshold}
                onChange={e => { setForm(p => ({ ...p, maxThreshold: e.target.value })); setErrors(p => ({ ...p, maxThreshold: undefined })); }} />
              {errors.maxThreshold && <span className="field-error">{errors.maxThreshold}</span>}
            </div>
            <div className={`sensor-form-group ${errors.samplingInterval ? 'has-error' : ''}`}>
              <label>Interval (sec)</label>
              <input type="number" value={form.samplingInterval}
                onChange={e => { setForm(p => ({ ...p, samplingInterval: e.target.value })); setErrors(p => ({ ...p, samplingInterval: undefined })); }} />
              {errors.samplingInterval && <span className="field-error">{errors.samplingInterval}</span>}
            </div>
          </div>
          <div className="sensor-form-actions">
            <button className="btn-secondary" onClick={() => { setAdding(false); setErrors({}); }}>Cancel</button>
            <button className="btn-primary" onClick={handleAdd}>Add Sensor</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SensorsPanel;
